import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';

import { API_URLS } from '../common/config';
import type { ListingsAPIErrorResponseBody } from '../common/types/ListingsAPIErrorResponseBody';
import type { ListingsAPIRequestBody } from './types/ListingsAPIRequestBody';
import type { ListingsAPIResponseBody } from './types/ListingsAPIResponseBody';

const DEFAULT_TIMEOUT = 5000;

interface FetchListingsDataArgs {
  body: ListingsAPIRequestBody;
  bot?: boolean; // true if the request is for a bot
  clientSide?: boolean;
  env: 'qa' | 'prod';
  servingConfig?: string;
  timeout?: number;
  // Full request URL, overriding the built-in one for `env`. Lets a consumer
  // drive the URL from its own config rather than this package's defaults.
  url?: string;
  userId?: string;
  visitorId?: string;
}

export const fetchListingsData = async ({
  body,
  bot = false,
  clientSide = false,
  env,
  servingConfig,
  timeout = DEFAULT_TIMEOUT,
  url,
  userId,
  visitorId,
}: FetchListingsDataArgs): Promise<ListingsAPIResponseBody | ListingsAPIErrorResponseBody> => {
  const envConfig = API_URLS.listings[env];
  const requestUrl = url ?? (clientSide ? envConfig.clientSide : envConfig.serverSide);
  if (!visitorId) {
    throw new Error(`visitorId must be specified`);
  }

  const apiResponse = await fetchJSONWithTimeout(requestUrl, {
    method: 'POST',
    timeout,
    headers: {
      ...(bot && { 'noths-is-robot': 'true' }),
      ...(userId && { 'noths-user-id': userId }),
      'noths-visitor-id': visitorId,
      'Content-Type': 'application/json',
      ...(servingConfig && { 'noths-serving-config': servingConfig }),
    },
    body: body as unknown as Record<string, unknown>,
  });

  const data = await apiResponse.json();

  if (apiResponse.ok) {
    return data as ListingsAPIResponseBody;
  }

  if (data.message) {
    return data as ListingsAPIErrorResponseBody;
  }

  return data;
};
